// @owners { team: patients-team }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery } from '@tanstack/react-query';
import { useGetPaymentMethods } from './paymentMethods';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { type PaymentMethodWithAmount } from '~shared/features/payments/types';
import { get } from '~shared/helpers/apiHelper';
import { staleTimes } from '~shared/queries/constants';
import { useSelectorShared } from '~shared/store';
import { type PaymentMethod, type ShipmentPaymentMethod } from '~shared/types';

export const shipmentPaymentMethods = createQueryKeys('shipmentPaymentMethods', {
  fetchAll: () => ({
    queryKey: ['fetch-all-shipment-payment-methods'],
    queryFn: async () => {
      const response = await get('/shipment_payment_methods');
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response as ShipmentPaymentMethod[];
    },
  }),
});

/**
 * Fetch all shipment payment methods for a patient.
 * Runs only when authorized; `options.enabled` is AND-ed with auth (defaults to true when omitted).
 * @returns Array of shipment payment methods
 */
export const useGetShipmentPaymentMethods = (options?: { enabled?: boolean }) => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const enabled = isAuthorized && (options?.enabled ?? true);
  const { data, isLoading, isSuccess, error } = useQuery({
    ...shipmentPaymentMethods.fetchAll(),
    enabled,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return {
    error,
    isLoading,
    isSuccess,
    shipmentPaymentMethods: data ?? [],
  };
};

export const useGetShipmentPaymentMethodsWithDetails = ({ shipmentID }: { shipmentID: number }) => {
  const { paymentMethods } = useGetPaymentMethods();
  const { shipmentPaymentMethods } = useGetShipmentPaymentMethods();

  const paymentMethodsByID = paymentMethods.reduce<Record<number, PaymentMethod>>((methods, method) => {
    methods[method.id] = method;
    return methods;
  }, {});

  return shipmentPaymentMethods
    .filter((spm) => spm.shipment_id === shipmentID)
    .map((method) => {
      const { payment_method_id } = method;
      const paymentMethod: PaymentMethodWithAmount = {
        ...method,
        paymentMethod: paymentMethodsByID[payment_method_id] || undefined,
      };
      return paymentMethod;
    });
};
